import {
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
} from '../../../constants/api'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../../constants/rentalPortalMockState'
import { APPLICANT_DETAILS_SNAPSHOT } from '../../../utils/rentalPortalMockData'
import { hasRentalPortalMockState } from '../../../utils/rentalPortalMockState'

const MOCK_APPLICANT_COMPLIANCE_READ_LATENCY = 300

const MOCK_APPLICANT_COMPLIANCE_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_APPLICANT_COMPLIANCE_WRITE_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_APPLICANT_COMPLIANCE_NOT_FOUND_ERROR = {
  response: { status: HTTP_STATUS_NOT_FOUND },
}

const MOCK_APPLICANT_COMPLIANCE_NOT_STARTED_CASE = 'not_started'

const MOCK_APPLICANT_HANDOFF_STATUS = {
  queued: 'queued',
  sent: 'sent',
}

const MOCK_APPLICANT_COMPLIANCE_BY_ID = new Map(
  APPLICANT_DETAILS_SNAPSHOT.records.map(record => [
    record.id,
    record.compliance_case,
  ])
)

const MOCK_APPLICANT_HANDOFF_RETRY_BY_ID = new Map()

function getMockApplicantComplianceCase(applicantId) {
  const caseKey = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
    ? MOCK_APPLICANT_COMPLIANCE_NOT_STARTED_CASE
    : (MOCK_APPLICANT_COMPLIANCE_BY_ID.get(applicantId) ??
      APPLICANT_DETAILS_SNAPSHOT.created_applicant_record.compliance_case)

  return APPLICANT_DETAILS_SNAPSHOT.compliance_cases[caseKey] ?? null
}

function getMockApplicantHandoffExternalId({ applicantId, handoff }) {
  return handoff.dispatched_at
    ? `RP5-APP-${String(applicantId).padStart(4, '0')}`
    : null
}

export function getMockApplicantHandoffState(applicantId) {
  const id = Number(applicantId)
  const handoff = getMockApplicantComplianceCase(id)?.handoff

  return handoff
    ? { ...handoff, ...MOCK_APPLICANT_HANDOFF_RETRY_BY_ID.get(id) }
    : null
}

function toServedMockApplicantHandoff({ applicantId, handoff }) {
  return {
    ...handoff,
    external_id: getMockApplicantHandoffExternalId({ applicantId, handoff }),
    permissions: {
      can_retry: hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.allRights),
    },
  }
}

function withMockApplicantComplianceLatency() {
  const delay = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
    ? RENTAL_PORTAL_MOCK_LATENCY
    : MOCK_APPLICANT_COMPLIANCE_READ_LATENCY

  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

export function getMockApplicantCompliance({ applicantId }) {
  return withMockApplicantComplianceLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_APPLICANT_COMPLIANCE_READ_ERROR)
    }

    const id = Number(applicantId)
    const complianceCase = getMockApplicantComplianceCase(id)

    return complianceCase
      ? {
          data: {
            handoff: toServedMockApplicantHandoff({
              applicantId: id,
              handoff: getMockApplicantHandoffState(id),
            }),
            screening: complianceCase.screening,
          },
        }
      : Promise.reject(MOCK_APPLICANT_COMPLIANCE_NOT_FOUND_ERROR)
  })
}

export function postMockApplicantHandoffRetry({ applicantId }) {
  return withMockApplicantComplianceLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_APPLICANT_COMPLIANCE_WRITE_ERROR)
    }

    const id = Number(applicantId)
    const handoff = getMockApplicantHandoffState(id)

    if (!handoff) {
      return Promise.reject(MOCK_APPLICANT_COMPLIANCE_NOT_FOUND_ERROR)
    }

    MOCK_APPLICANT_HANDOFF_RETRY_BY_ID.set(id, {
      attempts: handoff.attempts + 1,
      last_error: null,
      status: handoff.dispatched_at
        ? MOCK_APPLICANT_HANDOFF_STATUS.sent
        : MOCK_APPLICANT_HANDOFF_STATUS.queued,
      updated_at: new Date().toISOString(),
    })

    return {
      data: {
        handoff: toServedMockApplicantHandoff({
          applicantId: id,
          handoff: getMockApplicantHandoffState(id),
        }),
      },
    }
  })
}
